import { collection, documentId, getDocs, query, where, type DocumentData } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';

// Roster reads with `in` queries instead of one read per person (#73, #117).

/** Firestore's `in` filter takes at most 30 values. */
export const IN_CHUNK = 30;
/** How many chunks are read at once. */
export const CHUNK_CONCURRENCY = 6;

/** Distinct, non-empty ids in chunks of `size` (first-seen order kept). */
export function chunkIds(ids: string[], size: number = IN_CHUNK): string[][] {
  const unique = Array.from(new Set(ids)).filter(Boolean);
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += size) chunks.push(unique.slice(i, i + size));
  return chunks;
}

/** Runs `run` over `items` with at most `limit` in flight. */
export async function forEachLimited<T>(items: T[], limit: number, run: (item: T) => Promise<void>): Promise<void> {
  let next = 0;
  const worker = async () => {
    while (next < items.length) await run(items[next++]);
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}

/** Rows grouped by a string field, each group in the rows' order. */
export function groupBy<T extends DocumentData>(rows: T[], field: string): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const r of rows) {
    const key = r[field];
    if (typeof key !== 'string') continue;
    const list = out.get(key);
    if (list) list.push(r);
    else out.set(key, [r]);
  }
  return out;
}

/**
 * Documents of one collection by id, 30 ids per `documentId() in` query and
 * CHUNK_CONCURRENCY chunks at once. A missing document is absent from the
 * map, as `getDoc(...).exists()` would be false. A failed chunk rejects the
 * whole read unless `onChunkError` is given, which then gets the chunk's ids
 * and the read goes on without them.
 */
export async function getDocsByIds(
  collectionName: string,
  ids: string[],
  onChunkError?: (ids: string[], error: unknown) => void
): Promise<Map<string, DocumentData>> {
  const out = new Map<string, DocumentData>();
  await forEachLimited(chunkIds(ids), CHUNK_CONCURRENCY, async (part) => {
    try {
      const snap = await getDocs(query(collection(db, collectionName), where(documentId(), 'in', part)));
      snap.docs.forEach((d) => out.set(d.id, d.data()));
    } catch (error) {
      if (!onChunkError) throw error;
      onChunkError(part, error);
    }
  });
  return out;
}
